import { useCallback, useEffect, useRef, useState } from "react";
import {
  getMonthlyRuntimeUsage,
  listMonthlyRuntimeUsage,
  type RuntimeSegmentUsageRow,
} from "@/api/usage";
import { useAuth } from "@/hooks/use-auth";
import type en from "@/i18n/messages/settings-usage/en";
type Summary = Awaited<ReturnType<typeof getMonthlyRuntimeUsage>>;
/** A settings-usage catalogue key; the page translates it when it renders. */
type UsageError = Extract<keyof typeof en, "loadFailed" | "loadMoreFailed">;
const utcMonth = () => new Date().toISOString().slice(0, 7);
/** Refresh the UTC calendar month even when the pocket stays open over midnight. */
function useUsagePeriod() {
  const { user } = useAuth();
  const [month, setMonth] = useState(utcMonth);
  useEffect(() => {
    const refresh = () => setMonth(utcMonth());
    const timer = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, []);
  return `${user?.id ?? ""}:${month}`;
}
export function useMonthlyUsageSummary() {
  const period = useUsagePeriod();
  const [summary, setSummary] = useState<Summary | null>(null);
  useEffect(() => {
    let active = true;
    setSummary(null);
    void getMonthlyRuntimeUsage()
      .then((value) => {
        if (active) setSummary(value);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [period]);
  return summary;
}
export function useMonthlyUsage() {
  const period = useUsagePeriod();
  const generation = useRef(0);
  const loadingMore = useRef(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<RuntimeSegmentUsageRow[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<UsageError | null>(null);
  const reload = useCallback(async () => {
    const request = ++generation.current;
    loadingMore.current = false;
    setMore(false);
    setLoading(true);
    setError(null);
    setSummary(null);
    setRows([]);
    setNextOffset(null);
    try {
      const [total, page] = await Promise.all([
        getMonthlyRuntimeUsage(),
        listMonthlyRuntimeUsage({ limit: 10 }),
      ]);
      if (request !== generation.current) return;
      setSummary(total);
      setRows(page.items);
      setNextOffset(page.next_offset);
    } catch {
      if (request === generation.current) setError("loadFailed");
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, []);
  const cancel = useCallback(() => {
    generation.current++;
    loadingMore.current = false;
  }, []);
  useEffect(() => {
    void reload();
    return cancel;
  }, [period, reload, cancel]);
  async function loadMore() {
    if (nextOffset === null || loadingMore.current || loading) return;
    loadingMore.current = true;
    setMore(true);
    setError(null);
    const request = generation.current;
    try {
      const page = await listMonthlyRuntimeUsage({
        offset: nextOffset,
        limit: 10,
      });
      if (request !== generation.current) return;
      setRows((current) => {
        const seen = new Set(current.map((row) => row.runtime_session_id));
        return [
          ...current,
          ...page.items.filter((row) => {
            if (seen.has(row.runtime_session_id)) return false;
            seen.add(row.runtime_session_id);
            return true;
          }),
        ];
      });
      setNextOffset(page.next_offset);
    } catch {
      if (request === generation.current) setError("loadMoreFailed");
    } finally {
      if (request === generation.current) {
        loadingMore.current = false;
        setMore(false);
      }
    }
  }
  return { summary, rows, nextOffset, loading, more, error, reload, loadMore };
}
